import {
  Box,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import type {
  SchemeManifest,
  SchemeTokenOverrides,
  FontRole,
} from "@shared/types/foyer";
import { FONT_CATALOGUE, fontOptionForStack } from "@shared/schemes/fonts";
import { fieldGrid } from "./editorLayout";

interface Props {
  manifest: SchemeManifest;
  value: SchemeTokenOverrides | undefined;
  onChange: (next: SchemeTokenOverrides) => void;
}

const CUSTOM = "__custom__";

const ROLE_KEYS = ["display", "serif", "sans", "mono"] as const;
type RoleKey = (typeof ROLE_KEYS)[number];
const isRoleKey = (role: string): role is RoleKey =>
  (ROLE_KEYS as readonly string[]).includes(role);

/**
 * Pick the font for each of the template's text roles from a curated catalogue
 * (all already loaded, so they render immediately), with a "Custom…" escape to
 * a raw CSS font-family stack. Writes a sparse `tokens.fonts` diff. Changing a
 * role updates every text element that uses it.
 */
export default function TypographyEditor({ manifest, value, onChange }: Props) {
  const { t } = useTranslation("templates");
  const defaults = manifest.defaultTokens;
  const fontRoles = Object.keys(defaults.fonts) as FontRole[];
  const setFont = (role: FontRole, v: string) =>
    onChange({ ...value, fonts: { ...value?.fonts, [role]: v } });
  const fontVal = (role: FontRole) =>
    value?.fonts?.[role] ?? defaults.fonts[role] ?? "";

  const groups: { label: string; cat: "serif" | "sans" | "mono" }[] = [
    { label: t("typographyEditor.groups.serif"), cat: "serif" },
    { label: t("typographyEditor.groups.sans"), cat: "sans" },
    { label: t("typographyEditor.groups.mono"), cat: "mono" },
  ];

  return (
    <Stack spacing={2}>
      <Typography variant="h6">{t("typographyEditor.title")}</Typography>
      <Typography variant="body2" color="text.secondary">
        <Trans
          t={t}
          i18nKey="typographyEditor.intro"
          components={{ strong: <strong /> }}
        />
      </Typography>
      <Box sx={fieldGrid(320)}>
        {fontRoles.map((role) => {
          const v = fontVal(role);
          const matched = fontOptionForStack(v);
          return (
            <Box key={role}>
              <TextField
                select
                size="small"
                fullWidth
                label={
                  isRoleKey(role) ? t(`typographyEditor.roles.${role}`) : role
                }
                value={matched ? matched.stack : CUSTOM}
                onChange={(e) => setFont(role, e.target.value)}
              >
                {groups.flatMap((g) => [
                  <ListSubheader key={`h-${g.cat}`}>{g.label}</ListSubheader>,
                  ...FONT_CATALOGUE.filter((f) => f.category === g.cat).map(
                    (f) => (
                      <MenuItem
                        key={f.stack}
                        value={f.stack}
                        sx={{ fontFamily: f.stack }}
                      >
                        {f.label}
                      </MenuItem>
                    )
                  ),
                ])}
                <ListSubheader key="h-other">
                  {t("typographyEditor.groups.other")}
                </ListSubheader>
                <MenuItem value={CUSTOM}>
                  {t("typographyEditor.custom")}
                </MenuItem>
              </TextField>
              {!matched && (
                <TextField
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  label={t("typographyEditor.customStack")}
                  value={v}
                  onChange={(e) => setFont(role, e.target.value)}
                  helperText={t("typographyEditor.customStackHelp")}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}
